// Day content configuration - RESTRUCTURED 7-day flow
export const dayContent = {
  1: {
    title: "Pain Points + World Problems",
    phase: "Clarity",
    time: "90 minutes",
    scripture: {
      text: "Let each of you look not only to his own interests, but also to the interests of others.",
      reference: "Philippians 2:4"
    },
    prayer: `Lord, I come before You at the beginning of this journey asking for three things.

Courage to accept the assignments You've already placed in front of me. Help me stop calling fear wisdom and start calling obedience faith.

Discernment to know what problems are mine to solve, when the timing is right, and how to build with integrity. Let me see the world through eyes of purpose, not just opportunity.

Discipline to follow through. Not just today, but through the hard days when momentum fades and distraction is loud. Let this be the week I stop waiting and start building.`,
    narrativeHook: `Today we start with the world, not with you.

Before we talk about your skills or your story, I want you to pay attention to pain. Every successful business solves a real problem. Harvard Business School Professor Clayton Christensen calls this 'Jobs to Be Done' — people don't buy products, they hire solutions to do a job they need done.

So today, your job is to notice.

What slows people down? What wastes time or costs money? What feels unfair or broken? What do people keep complaining about — at work, at church, in their community, in your own life?

Your business idea is hiding inside a problem you've already lived or witnessed. Let's find it.`,
    examples: [
      {
        painPoint: "It's hard to start a business alone with no direction or accountability",
        who: "Aspiring founders, especially women of faith",
        existingSolutions: "YouTube, random PDFs, scattered advice",
        missing: "A structured sprint with community and real next steps"
      },
      {
        painPoint: "Finding safe, affordable after-school care with flexible hours",
        who: "Working parents in my city",
        existingSolutions: "YMCA, private sitters, family help",
        missing: "Trustworthy, affordable, flexible options"
      },
      {
        painPoint: "Meeting a spouse as a person of faith in a world built for casual dating",
        who: "Abstinent singles, especially Christians",
        existingSolutions: "Dating apps, social media, church groups",
        missing: "A platform that honors faith, boundaries, and intentional community"
      }
    ],
    matrixPrompt: "Think about what irritates you, what frustrates the people around you, and what problems you see repeatedly going unsolved. Fill in as many rows as you can. You don't have to finish in one sitting.",
    exercises: [
      {
        id: "pain_matrix",
        title: "Pain Point Matrix",
        instructions: "Track 10 problems you notice in work, life, church, or community. Be specific. Think about what makes people frustrated, stuck, or overwhelmed.",
        type: "matrix",
        columns: ["Pain Point", "Who Faces This", "Existing Solutions", "What's Missing"],
        rows: 10
      }
    ],
    socialProof: [
      {
        name: "Sarah",
        location: "Atlanta",
        quote: "I had 17 ideas on my brain dump. When I stepped back and looked at the tags, 9 of them had 'S' - story-connected. They were all about childcare. Turns out my personal struggle WAS my business idea."
      },
      {
        name: "Marcus",
        location: "Chicago",
        quote: "Day 1 scared me. Writing down every problem felt like admitting I'd been stuck for 2 years. But seeing the pattern — I kept coming back to financial literacy for first-gen students — was the clarity I needed."
      },
      {
        name: "Keisha",
        location: "Houston",
        quote: "The pain point matrix was a gut punch. I'd been downplaying problems in my community because they felt 'too big.' Turns out, starting small IS the answer."
      }
    ],
    aiButton: {
      label: "Analyze My Pain Points",
      prompt: "pattern_analysis",
      minRows: 3,
      lockedMessage: "Fill in at least 3 pain points to unlock your AI analysis."
    },
    reflection: {
      prompt: "Which pain point made me feel something real — anger, grief, conviction?",
      prayerFocus: "Ask God which of these problems He's calling you to solve. Sit with it. The one that won't leave you alone is probably your assignment."
    },
    celebration: {
      title: "DAY 1 COMPLETE",
      achievements: ["Documented 10 real-world problems", "Identified top 3 urgent pain points", "Found patterns in who struggles"],
      message: "You now see the world differently. That's the first step."
    }
  },
  2: {
    title: "Strengths Audit",
    phase: "Clarity",
    time: "90 minutes",
    scripture: {
      text: "Each of you should use whatever gift you have received to serve others, as faithful stewards of God's grace.",
      reference: "1 Peter 4:10"
    },
    prayer: `Lord, today I ask You to help me see myself clearly.

Not through the lens of comparison. Not who I am next to her, or him, or who I think I should be by now. But through Your eyes. The gifts You placed in me on purpose, for a purpose.

Help me stop calling my strengths 'just things I do' and start recognizing them as tools You've already equipped me with for the work ahead.

Where I've been tempted to downplay my story because it feels too personal, too small, or too obvious - give me boldness. The thing that feels easy to me is someone else's breakthrough.`,
    narrativeHook: `Yesterday you looked at the world's problems. Today we look at you.

God doesn't give assignments without also giving the equipment. Your strengths, your lived experiences, and your story are not random. They are the reason you are uniquely positioned to solve a specific problem for a specific person.

This is what startups call Founder-Market Fit: the idea that YOU specifically are the right person to build this specific solution. Not because you're perfect, but because your path prepared you.

Three questions to sit with today:
What do people ask you for help with without you advertising?
Where have you overcome something others still struggle with?
What feels completely natural to you but seems hard or mysterious to everyone else?

That intersection is your unfair advantage. Let's map it.`,
    strengthExamples: [
      "People always come to me for resume help and LinkedIn rewrites. I love making messy careers look clear and compelling.",
      "I grew up bilingual and watched my parents struggle with school forms and government documents. I became the family translator for language and for systems.",
      "I struggled with anxiety for years but God gave me tools that actually worked. Now I share them informally at church and people keep asking for more.",
      "I've been doing people's hair since I was 14. What feels like chitchat to me is actually deep listening. I always know what someone really needs."
    ],
    strengthsPrompt: "List at least 5. Include professional skills, lived experiences, personality gifts, and things people compliment you on that you tend to dismiss.",
    bridgeExampleRow: {
      strength: "Teaching complex things simply",
      proof: "Trained 12 new hires at my job",
      who: "First-gen professionals who feel behind",
      ideas: "Career coaching, online course, workshop"
    },
    askThreePeople: {
      header: "Now ask 3 people.",
      body: "Copy and paste this message to 3 people who know you well - a friend, a coworker, a family member, someone from church:",
      message: "Hey, quick question — I'm doing a 7-day business clarity sprint and your honest answer would really help. What do you think is my top strength or gift? What do people come to you for? What do you think I'm uniquely good at that I might be undervaluing?"
    },
    exercises: [
      {
        id: "strengths_list",
        title: "List 5-7 Strengths with Proof",
        instructions: "Don't just say 'I'm organized.' Say 'I'm organized — I managed a team of 12 and never missed a deadline.' Proof makes it real.",
        type: "strengths",
        fields: ["strength", "proof"],
        rows: 7
      },
      {
        id: "strength_bridge",
        title: "Strength-to-Business Bridge",
        instructions: "Now connect your strengths to potential business value.",
        type: "bridge_table",
        columns: ["My Strength", "Proof", "Who Benefits", "Business Ideas"],
        rows: 5
      }
    ],
    aiButton: {
      label: "Generate My Unfair Advantage Statement",
      prompt: "unfair_advantage",
      minStrengths: 3,
      lockedMessage: "Add at least 3 strengths to unlock your Unfair Advantage analysis."
    },
    reflection: {
      prompt: "Which strength surprised you today - one you almost didn't write down because it felt too obvious or too personal?",
      prayerFocus: "Ask God to confirm which gift connects to the problem He's calling you to solve. The overlap between Day 1 and Day 2 is where your business lives."
    },
    celebration: {
      title: "DAY 2 COMPLETE",
      achievements: ["Documented 5-7 strengths with proof", "Connected strengths to business value", "Generated your Unfair Advantage Statement"],
      message: "You know what you bring to the table. Now let's build on it."
    }
  },
  3: {
    title: "Idea Generation",
    phase: "Validation",
    time: "75 minutes",
    contextCards: ["top_pain_points", "top_strengths"],
    scripture: {
      text: "Plans fail for lack of counsel, but with many advisers they succeed.",
      reference: "Proverbs 15:22"
    },
    prayer: `God, give me wisdom to choose well. There are many ideas I could pursue — show me the ONE that aligns with Your purpose for my life. Help me trust the process and not rush past this moment. I want to build something that matters, not just something that sounds good. In Jesus' name, Amen.`,
    narrativeHook: `Now we bring it together.

You've documented real problems. You've audited your strengths. Today, we let AI help us see what YOU might be missing — the business ideas that sit at the intersection of what the world needs and what you're uniquely gifted to provide.

This is where clarity becomes action.`,
    aiButton: {
      label: "Generate My Business Ideas",
      prompt: "generate_ideas"
    },
    ideaSelection: {
      instructions: "Review the generated ideas, then select ONE to carry forward. This will become your focus for the rest of the sprint.",
      selectPrompt: "I choose this idea"
    },
    reflection: {
      prompt: "Which idea would I build even if no one cheered?",
      prayerFocus: "Ask God to confirm your selection with peace."
    },
    celebration: {
      title: "DAY 3 COMPLETE",
      achievements: ["Generated 3-5 business ideas", "Selected your ONE idea to build", "Confirmed founder-market fit"],
      message: "You have direction. Now let's scope it."
    }
  },
  4: {
    title: "MVP Scoping",
    phase: "Validation",
    time: "90 minutes",
    contextCards: ["selected_idea"],
    scripture: {
      text: "Commit your work to the LORD, and your plans will be established.",
      reference: "Proverbs 16:3"
    },
    prayer: `Lord, protect me from perfectionism. Help me embrace "good enough to test" over "perfect but never launched." Give me the courage to start small and the wisdom to know what to cut. I trust You with the outcome. In Jesus' name, Amen.`,
    narrativeHook: `Here's the truth most founders don't want to hear:

Your MVP should feel embarrassingly small. If you're not slightly uncomfortable with how simple it is, it's probably too big.

Today we scope your idea down to something you can actually build and test in 14 days.`,
    scopeComparison: {
      tooBig: { label: "TOO BIG (6 months)", example: "Full platform with user profiles, messaging, payment processing, mobile app, recommendation algorithm", color: "red" },
      stillTooBig: { label: "STILL TOO BIG (2-3 months)", example: "Website where users browse, book, and pay for services", color: "yellow" },
      rightSized: { label: "RIGHT-SIZED MVP (2 weeks)", example: "Landing page with waitlist form. When 10 people sign up, I manually reach out via Zoom/email. No platform. No automation. Just proof people want this.", color: "green" }
    },
    exercises: [
      {
        id: "smallest_delivery",
        title: "The 14-Day Test",
        question: "If 10 people said yes tomorrow, what's the SMALLEST thing you could deliver in 14 days?",
        type: "textarea"
      },
      {
        id: "not_building",
        title: "The NOT List",
        question: "What will you NOT build in version 1? List at least 5 things.",
        type: "checklist",
        minItems: 5
      },
      {
        id: "phase_planning",
        title: "Phase Planning",
        phases: [
          { id: "phase_1", label: "Phase 1 (MVP — NOW)", placeholder: "List 1-2 core features only" },
          { id: "phase_2", label: "Phase 2 (After first 10 customers)", placeholder: "List 2-3 features" },
          { id: "phase_3", label: "Phase 3 (Full vision — 6 months)", placeholder: "List 3-5 features" }
        ]
      }
    ],
    reflection: {
      prompt: "Does this feel embarrassingly small? Good. That means it's right.",
      prayerFocus: "Ask God to give you peace about starting small."
    },
    celebration: {
      title: "DAY 4 COMPLETE",
      achievements: ["Scoped your 14-day MVP", "Created your NOT list", "Planned your phases"],
      message: "You know exactly what to build first. Tomorrow we write the words."
    }
  },
  5: {
    title: "Landing Page Copy",
    phase: "Build",
    time: "75 minutes",
    contextCards: ["selected_idea", "mvp_scope"],
    scripture: {
      text: "Let your speech always be gracious, seasoned with salt, so that you may know how you ought to answer each person.",
      reference: "Colossians 4:6"
    },
    prayer: `God, give me words that connect. Help me speak to real pain and real hope — not hype, not manipulation, just truth. Let my message reach the right people with clarity and conviction. In Jesus' name, Amen.`,
    narrativeHook: `Words matter.

The difference between a landing page that converts and one that doesn't often comes down to whether you're speaking to the right pain in the right language.

Today, AI helps us write copy that speaks directly to your ideal customer. Then you customize it to sound like YOU.`,
    aiButton: {
      label: "Generate My Landing Page Copy",
      prompt: "generate_copy"
    },
    copyFields: [
      { id: "headline", label: "Headline", type: "input", placeholder: "Speaks to their pain" },
      { id: "subheadline", label: "Subheadline", type: "input", placeholder: "The transformation you offer" },
      { id: "benefit_1", label: "Benefit 1", type: "input", placeholder: "First key benefit" },
      { id: "benefit_2", label: "Benefit 2", type: "input", placeholder: "Second key benefit" },
      { id: "benefit_3", label: "Benefit 3", type: "input", placeholder: "Third key benefit" },
      { id: "cta", label: "Call to Action", type: "input", placeholder: "e.g., Join the Waitlist" },
      { id: "founder_story", label: "Founder Story", type: "textarea", placeholder: "2-3 sentences about why YOU built this" },
      { id: "faq_1", label: "FAQ 1", type: "textarea", placeholder: "Common objection and answer" },
      { id: "faq_2", label: "FAQ 2", type: "textarea", placeholder: "Common objection and answer" },
      { id: "faq_3", label: "FAQ 3", type: "textarea", placeholder: "Common objection and answer" }
    ],
    saveButton: {
      label: "Save My Copy",
      successMessage: "Copy saved! You'll use this tomorrow to build your landing page."
    },
    reflection: {
      prompt: "Does this copy sound like me? Would I trust this message if I were the customer?",
      prayerFocus: "Ask God to prepare the hearts of those who will read this."
    },
    celebration: {
      title: "DAY 5 COMPLETE",
      achievements: ["Generated landing page copy", "Customized your messaging", "Saved copy for Day 6"],
      message: "Your words are ready. Tomorrow we make them visual."
    }
  },
  6: {
    title: "Build Your Landing Page",
    phase: "Build",
    time: "120 minutes",
    contextCards: ["landing_page_copy"],
    loadSavedCopy: true,
    scripture: {
      text: "Whatever you do, work at it with all your heart, as working for the Lord, not for human masters.",
      reference: "Colossians 3:23"
    },
    prayer: `Lord, help me create something excellent. Remove perfectionism but maintain quality. Let this page represent the best of what I'm building — not because I need to impress, but because excellence honors You. In Jesus' name, Amen.`,
    narrativeHook: `This is the moment everything becomes REAL.

Your copy from yesterday is waiting. Now we turn those words into a living, breathing landing page that people can actually visit.

No coding required. Just one click.`,
    buildButton: {
      label: "Build My Landing Page",
      prompt: "build_landing_page"
    },
    fallbackMessage: "Here is your copy ready to paste into Carrd, Notion, or any landing page tool:",
    actions: [
      { id: "copy_html", label: "Copy HTML", icon: "copy" },
      { id: "preview", label: "Preview Full Page", icon: "external" },
      { id: "download", label: "Download HTML", icon: "download" }
    ],
    reflection: {
      prompt: "How does it feel to see your idea as something real that people can interact with?",
      prayerFocus: "Thank God for the courage to build. Ask Him to guide you as you share it."
    },
    celebration: {
      title: "DAY 6 COMPLETE",
      achievements: ["Built your landing page", "Created a shareable URL", "Made your idea REAL"],
      message: "You have a landing page. Most founders never get here."
    }
  },
  7: {
    title: "Validation + Decision",
    phase: "Launch",
    time: "120 minutes",
    contextCards: ["selected_idea", "icp_summary"],
    scripture: {
      text: "If any of you lacks wisdom, let him ask God, who gives generously to all without reproach, and it will be given him.",
      reference: "James 1:5"
    },
    prayer: `God, give me peace in my decision. Help me hear truth in the feedback I receive — not just what I want to hear. Whether the answer is GO, PIVOT, or RELEASE, I trust that You are ordering my steps. In Jesus' name, Amen.`,
    narrativeHook: `Today you stop guessing and start knowing.

You've built something real. Now it's time to talk to real people and make a real decision.

5 conversations. That's all. But these conversations will tell you more than 5 months of "research."`,
    validationScript: {
      template: "Hey [NAME], I'm exploring an idea to help [ICP] who struggle with [PAIN]. Have you ever dealt with this? If yes, what have you tried so far? If something solved it cleanly, would you pay for it? What would feel worth it to you?",
      autoFill: ["icp_name", "top_pain"]
    },
    conversationTracker: {
      count: 5,
      fields: [
        { id: "name", label: "Person's Name", type: "input" },
        { id: "has_problem", label: "Do they have this problem?", type: "select", options: ["YES", "NO", "SOMETIMES"] },
        { id: "tried", label: "What have they tried?", type: "textarea" },
        { id: "would_pay", label: "Would they pay?", type: "select", options: ["YES", "MAYBE", "NO"] },
        { id: "exact_phrases", label: "Exact phrases they used", type: "textarea" }
      ]
    },
    decisionGuide: {
      go: { range: "4-5 said YES", action: "GO — Launch publicly" },
      iterate: { range: "2-3 said MAYBE", action: "ITERATE — Adjust your message or offering" },
      pivot: { range: "0-1 said YES", action: "PIVOT — Different idea or different audience" }
    },
    decision: {
      options: [
        { value: "GO", emoji: "🟢", title: "GO", description: "The feedback was positive. People want this. I'm moving forward." },
        { value: "PIVOT", emoji: "🟡", title: "PIVOT", description: "The core idea is right but needs adjustment." },
        { value: "RELEASE", emoji: "🔴", title: "RELEASE", description: "This isn't the right idea right now. I'm releasing it with peace." }
      ]
    },
    commitment: {
      template: "I, [NAME], commit to [COMMITMENT]. For the next 14 days, I will [ACTION]. I will not delay because of [FEAR]. I trust God to order my steps."
    },
    shareableCard: {
      generate: true,
      template: "[FIRST_NAME] went from Prayer to Prototype in 7 days",
      subtitle: "Building: [BUSINESS_NAME]"
    },
    communityUpsell: {
      title: "Join the Builder Community",
      description: "You've completed the sprint. But this is just the beginning. Workshop new ideas, get feedback, and build alongside other Christian founders.",
      cta: "Join the Community Waitlist"
    },
    finalBlessing: "The LORD bless you and keep you. May He give you courage to build and wisdom to steward what He's given you. Go build something beautiful.",
    isLaunch: true
  }
};
